import React, {useEffect} from 'react';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {createStackNavigator, StackHeaderProps} from '@react-navigation/stack';
import {ThemeProvider} from '@rn-vui/themed';

import BrandHeader from '../components/BrandHeader';
import {AppRoute} from '../models/AppRoute';
import {HeaderConfig, RootRewardsFlowParamList} from '../models/navigationTypes';
import AccountNotFoundScreen from '../screens/rewards/AccountNotFoundScreen';
import RewardsActivateTermsAndConditionsScreen from '../screens/rewards/activate/RewardsActivateTermsAndConditionsScreen';
import RewardsActivateVerificationScreen from '../screens/rewards/activate/RewardsActivateVerificationScreen';
import LmgStaffSelectionScreen from '../screens/rewards/LmgStaffSelectionScreen';
import RewardsFullSignUpScreen from '../screens/rewards/RewardsFullSignUpScreen';
import RewardsFullSuccessScreen from '../screens/rewards/RewardsFullSuccessScreen';
import RewardsRedirectScreen from '../screens/rewards/RewardsRedirectScreen';
import RewardsSignInScreen from '../screens/rewards/RewardsSignInScreen';
import RewardsVerificationScreen from '../screens/rewards/RewardsVerificationScreen';
import RewardsWelcomeBackScreen from '../screens/rewards/RewardsWelcomeBackScreen';
import RewardsSignUp from '../screens/rewards/SignUp';
import StaffActivationBlockedScreen from '../screens/rewards/StaffActivationBlockedScreen';
import StaffStoreSelectionScreen from '../screens/rewards/StaffStoreSelectionScreen';
import RewardsSuccess from '../screens/rewards/Success';
import RewardsTermsAndConditions from '../screens/rewards/TermsAndConditions';
import {useStores} from '../stores/use-stores';
import rewardsTheme from '../theme/rewardsTheme';

import useCommonScreenOptions from './hooks/useCommonScreenOptions';

export const header =
  (config: HeaderConfig) =>
  ({navigation, route}: StackHeaderProps) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <BrandHeader navigation={navigation as any} route={route} config={config} />
  );

const Stack = createStackNavigator<RootRewardsFlowParamList>();

const RootRewardsFlowStackNavigator = () => {
  const {rewardsStore, globalStore} = useStores();
  const commonScreenOptions = useCommonScreenOptions(true);

  // Reset staff signup flag and clear deep link path when leaving the rewards flow.
  // Clearing delayedNavigationPath prevents App.tsx from replaying the rewards
  // deep link after store selection on fresh sessions (mobile + web).
  // Only clears rewards-scoped paths to avoid wiping unrelated deep links.
  useEffect(() => {
    return () => {
      rewardsStore.resetStaffFlow();
      if (globalStore.delayedNavigationPath?.startsWith('/rewards')) {
        globalStore.clearDelayedNavigationPath();
      }
    };
  }, [rewardsStore, globalStore]);

  return (
    <ThemeProvider theme={rewardsTheme}>
      <GestureHandlerRootView style={{flex: 1}} testID="gesture-handler-root-view">
        <Stack.Navigator detachInactiveScreens={true} screenOptions={commonScreenOptions}>
          <Stack.Screen
            name={AppRoute.REWARDS_SIGN_IN}
            component={RewardsSignInScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_WELCOME_BACK}
            component={RewardsWelcomeBackScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_STAFF_STORE_SELECTION}
            component={StaffStoreSelectionScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_LMG_STAFF_SELECTION}
            component={LmgStaffSelectionScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_SIGN_UP}
            component={RewardsSignUp}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_VERIFICATION}
            component={RewardsVerificationScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_SUCCESS}
            component={RewardsSuccess}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED}
            component={StaffActivationBlockedScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_ACCOUNT_NOT_FOUND}
            component={AccountNotFoundScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_TERMS_AND_CONDITIONS}
            component={RewardsTermsAndConditions}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_FULL_SIGN_UP}
            component={RewardsFullSignUpScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_FULL_SUCCESS}
            component={RewardsFullSuccessScreen}
            options={{
              header: header({
                back: false,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />

          {/**
           * Rewards Activate flow
           */}
          <Stack.Screen
            name={AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS}
            component={RewardsActivateTermsAndConditionsScreen}
            options={{
              header: header({
                back: true,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_ACTIVATE_VERIFICATION}
            component={RewardsActivateVerificationScreen}
            options={{
              header: header({
                back: true,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_ACTIVATE_SUCCESS}
            component={RewardsSuccess}
            options={{
              header: header({
                back: true,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
          <Stack.Screen
            name={AppRoute.REWARDS_REDIRECT}
            component={RewardsRedirectScreen}
            options={{
              header: header({
                back: true,
                shop: false,
                filter: false,
                burger: false,
                search: false,
                blinkerMode: true,
              }),
            }}
          />
        </Stack.Navigator>
      </GestureHandlerRootView>
    </ThemeProvider>
  );
};

export default RootRewardsFlowStackNavigator;
